import { resolveActionCards } from './resolveActionCards';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Button, Checkbox, Cluster, ParticipantFace, Row, Stack, Switch, Typography, CloseIcon, showDialog, showToast } from '@src/components';
import { useAppSelector } from '@src/state/hooks';
import { useBoxExclusions } from './useBoxExclusions';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import './GameExclusionsDialog.css';
import { isDrawImpossible } from '@src/pages/RandomizerPage/draft';
import { santaClient } from '@src/state/client';

function GameExclusions({ info: initialInfo, cards: initialCards, onChanged }: {
  info: BoxInfo; cards: CardInfo[]; onChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const loaded = useAppSelector(state => state.box);
  const info = loaded.info?.box.key === initialInfo.box.key ? loaded.info : initialInfo;
  // The organizer page deliberately keeps no full list in Redux for paged boxes.
  // Keep the on-demand list in the dialog, including after nested edits.
  const [cards, setCards] = useState(initialCards);
  async function refreshCards() {
    await onChanged();
    const response = await santaClient.getCards({ key: initialInfo.box.key });
    if (!response.ok || !response.data) throw new Error('Participants unavailable');
    setCards(response.data);
  }
  const edit = useBoxExclusions(info, cards, refreshCards);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [onlyWithExclusions, setOnlyWithExclusions] = useState(false);
  const currentExclusions = (card: CardInfo) => cards.filter(person => person.id !== card.id && card.exclusions?.includes(person.id));
  const visibleCards = onlyWithExclusions ? cards.filter(card => currentExclusions(card).length) : cards;
  const allOpen = visibleCards.length > 0 && visibleCards.every(card => revealed.has(card.id));
  const canEdit = info.isAdmin && !info.isActiveDraw && !info.box.isArchived;
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  async function saveExclusions(card: CardInfo, nextIds: number[]) {
    if (!canEdit || savingRef.current) return;
    const draft = {
      gameName: info.box.name,
      names: cards.map(person => String(person.id)),
      exclusionsByName: Object.fromEntries(cards.map(person => [String(person.id),
        (person.id === card.id ? nextIds : currentExclusions(person).map(recipient => recipient.id)).map(String)])),
    };
    if (isDrawImpossible(draft, Boolean(info.box.useCircleDraw)) && nextIds.some(id => !card.exclusions?.includes(id))) {
      showToast(t('randomizer.impossible'));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await santaClient.setBoxCardExclusions({
        key: info.box.key,
        cardId: card.id,
        cardIds: nextIds,
      });
      if (!result.ok) throw new Error('exclusions save failed');
      await refreshCards();
    } catch {
      showToast(t('newBox.saveFailed'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  return <Stack gap={4}>
    <Typography as="p" role="meta/helper">{t('gameExclusions.description')}</Typography>
    <Cluster justify="between" wide className="game-exclusions-toolbar">
      <div><Checkbox checked={onlyWithExclusions} onCheckedChange={setOnlyWithExclusions} label={t('gameExclusions.onlyWithExclusions')} /></div>
      <Button variant="secondary" size="small" disabled={!visibleCards.length}
        onClick={() => setRevealed(allOpen ? new Set() : new Set(visibleCards.map(card => card.id)))}
        testId="game-exclusions-expand-all">{t(allOpen ? 'gameExclusions.collapseAll' : 'gameExclusions.expandAll')}</Button>
    </Cluster>
    {visibleCards.length === 0 && <Typography role="body/base">{t(onlyWithExclusions ? 'gameExclusions.noMatches' : 'game.people.noParticipants')}</Typography>}
    {visibleCards.map((card, index) => {
      const visible = revealed.has(card.id);
      const exclusions = visible ? currentExclusions(card) : [];
      const contentId = `game-exclusions-content-${card.id}`;
      return <Row as="div" key={card.id} testId={`game-exclusions-${card.id}`}>
        <div className="game-exclusions-row">
          <ParticipantFace card={card} index={index} size="row" />
          <Stack gap={1} wide>
            <Typography role="body/strong">{card.username}</Typography>
            <Typography as="p" role="meta/helper">{visible ? t('gameExclusions.count', { count: exclusions.length }) : t('gameExclusions.hidden')}</Typography>
          </Stack>
          <Button variant="ghost" size="small" aria-expanded={visible} aria-controls={contentId}
            icon={<ChevronDownIcon className={visible ? 'game-exclusions-chevron--open' : undefined} />}
            onClick={() => setRevealed(current => {
              const next = new Set(current); if (next.has(card.id)) next.delete(card.id); else next.add(card.id); return next;
            })}>{t(visible ? 'gameExclusions.collapse' : 'gameExclusions.show')}</Button>
        </div>
        {visible && <div className="game-exclusions-details" id={contentId}>
          <Stack gap={2}>
            <Typography as="p" role="meta/helper">{t('gameExclusions.cannotGive')}</Typography>
            {exclusions.length ? <ul className="game-exclusions-recipients">{exclusions.map(person => (
              <li key={person.id} className="game-exclusions-recipient" data-testid={`game-exclusion-${card.id}-${person.id}`}>
                <ParticipantFace card={person} index={cards.findIndex(candidate => candidate.id === person.id)} size="row" />
                <Typography role="body/strong" className="game-exclusions-recipient__name">{person.username}</Typography>
                <div className="game-exclusions-recipient__mutual"><Switch checked={Boolean(person.exclusions?.includes(card.id))}
                    label={t('gameExclusions.mutual')}
                    disabled={!canEdit || saving}
                    testId={`game-exclusion-mutual-${card.id}-${person.id}`}
                    onCheckedChange={mutual => {
                      const reverse = currentExclusions(person).map(recipient => recipient.id).filter(id => id !== card.id);
                      void saveExclusions(person, mutual ? [...reverse, card.id] : reverse);
                    }} /></div>
                {canEdit && <Button variant="ghost" size="small" icon={<CloseIcon />}
                  aria-label={t('gameExclusions.remove', { name: person.username })}
                  disabled={saving} onClick={() => void saveExclusions(card, currentExclusions(card).map(recipient => recipient.id).filter(id => id !== person.id))} />}
              </li>
            ))}</ul>
              : <Typography role="meta/helper">{t('gameExclusions.empty')}</Typography>}
            {canEdit && <Cluster justify="end"><Button variant="ghost" size="small" icon={<PencilIcon />} disabled={saving} onClick={() => edit(card, true)}>{t('gameExclusions.edit')}</Button></Cluster>}
          </Stack>
        </div>}
      </Row>;
    })}
  </Stack>;
}

export function useGameExclusionsDialog(info: BoxInfo, source: CardInfo[] | (() => Promise<CardInfo[]>), onChanged: () => Promise<void> | void) {
  const { t } = useTranslation();
  return async () => {
    const cards = await resolveActionCards(source);
    if (!cards) return;
    if (!info.isAdmin) return;
    showDialog({ title: t('gameExclusions.title'), closeLabel: t('dialog.close'), mobileFullScreen: true,
      testId: 'game-exclusions-dialog', children: <GameExclusions info={info} cards={cards} onChanged={onChanged} /> });
  };
}
